import React, { useEffect, useState } from "react";
import axios from "axios";
import "./RightUpdatesPanel.css";
import { FaBell } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const DEFAULT_WIDTH = 300;

// REAL ENDPOINTS
const LATEST_URL = "https://rohanbackend.axiomos.in/latest-activity";
const PROJECT_OVERVIEW_URL =
  "https://rohanbackend.axiomos.in/latest-activity/count";

const VIEW_MODES = {
  LATEST: "latest",
  OVERVIEW: "overview",
};

// Range keys must match backend keys
const RANGE_KEYS = {
  TODAY: "today",
  YESTERDAY: "yesterday",
  WEEK: "week",
  MONTH: "month",
  YEAR: "year",
};

const RANGE_LABELS = {
  [RANGE_KEYS.TODAY]: "Today",
  [RANGE_KEYS.YESTERDAY]: "Yesterday",
  [RANGE_KEYS.WEEK]: "This Week",
  [RANGE_KEYS.MONTH]: "Last 30 Days",
  [RANGE_KEYS.YEAR]: "This Year",
};

// Optional label mapping for modules
const MODULE_LABELS = {
  induction: "Induction",
  uauc: "UAUC",
  specific: "Specific Meeting",
  goodpractices: "Good Practices",
  workpermit: "Work Permit",
  meeting: "TBT / Meeting",
  workpermitdaily: "Work Permit Daily",
  incident: "Incident",
  inspection: "Inspection",
  violation: "Violation",
};

// Map API model → route segment (adjust if needed)
const MODEL_ROUTE_MAP = {
  uauc: "uauc",
  induction: "safetyinduction",
  meeting: "tbtmeeting",
  incident: "incident",
  inspection: "inspection",
  specific: "specific",
  goodpractices: "goodpractices",
  violation: "violation",
  workpermit: "workpermit",
  workpermitdaily: "workpermitdaily",
};

// Helper to build description line for "Latest"
const getUpdateDescription = (u) => {
  if (u.observation) return u.observation;
  if (u.violationDetails) return u.violationDetails;
  if (u.description) return u.description;

  if (u.model === "meeting") {
    return `TBT / Meeting – Attendees: ${u.attendeesNos || "-"}`;
  }

  if (u.model === "induction") {
    return "Induction session recorded.";
  }

  return u.project || "New record created.";
};

const RightUpdatesPanel = () => {
  const [viewMode, setViewMode] = useState(VIEW_MODES.LATEST);

  const [latestUpdates, setLatestUpdates] = useState([]);
  const [projectOverview, setProjectOverview] = useState([]);
  const [overviewRanges, setOverviewRanges] = useState(null); // optional, if you want to show actual dates later

  // Which range to show in Project Overview
  const [overviewRange, setOverviewRange] = useState(RANGE_KEYS.TODAY);

  const [width] = useState(() => {
    return (
      parseInt(localStorage.getItem("rightPanelWidth"), 10) || DEFAULT_WIDTH
    );
  });

  const [isOpen, setIsOpen] = useState(() => {
    const saved = localStorage.getItem("rightPanelOpen");
    return saved ? saved === "true" : false;
  });

  const navigate = useNavigate();

useEffect(() => {
  let isMounted = true;
  let requestInProgress = false;

  const fetchAll = async () => {
    /*
     * Prevent overlapping requests if the previous polling request
     * has not completed.
     */
    if (requestInProgress) {
      return;
    }

    requestInProgress = true;

    try {
      const results = await Promise.allSettled([
        axios.get(LATEST_URL),
        axios.get(PROJECT_OVERVIEW_URL),
      ]);

      if (!isMounted) {
        return;
      }

      const [latestResult, overviewResult] = results;

      // =====================================================
      // LATEST ACTIVITIES
      // =====================================================

      if (latestResult.status === "fulfilled") {
        const data = latestResult.value?.data || {};

        const list =
          (Array.isArray(data.latest100prioritized) &&
            data.latest100prioritized) ||
          (Array.isArray(data.allRecords) && data.allRecords) ||
          [];

        setLatestUpdates(list);
      } else {
        console.error(
          "Latest activity request failed:",
          latestResult.reason?.response?.data ||
            latestResult.reason?.message ||
            latestResult.reason,
        );
      }

      // =====================================================
      // PROJECT OVERVIEW
      // =====================================================

      if (overviewResult.status === "fulfilled") {
        const data = overviewResult.value?.data || {};

        const projects = Array.isArray(data.projects) ? data.projects : [];

        setProjectOverview(projects);
        setOverviewRanges(data.ranges || null);
      } else {
        console.error(
          "Project overview request failed:",
          overviewResult.reason?.response?.data ||
            overviewResult.reason?.message ||
            overviewResult.reason,
        );
      }
    } catch (error) {
      if (isMounted) {
        console.error(
          "Right Panel Fetch Error:",
          error?.response?.data || error?.message || error,
        );
      }
    } finally {
      requestInProgress = false;
    }
  };

  fetchAll();

  const interval = window.setInterval(fetchAll, 15000);

  return () => {
    isMounted = false;
    window.clearInterval(interval);
  };
}, []);

  /* ========== EXPOSE WIDTH TO LAYOUT ========== */
  useEffect(() => {
    document.documentElement.style.setProperty(
      "--right-panel-width",
      isOpen ? `${width}px` : "0px",
    );
    localStorage.setItem("rightPanelOpen", isOpen ? "true" : "false");
  }, [isOpen, width]);

  // On click of an update-item in "Latest" → /edit/{model}/{id}
  const handleItemClick = (item) => {
    if (!item) return;

    const modelKey = item.model;
    const routeSegment = MODEL_ROUTE_MAP[modelKey] || modelKey;

    const id = item.recordId || item.reportId;
    if (!routeSegment || !id) return;

    navigate(`/edit/${routeSegment}/${id}`);
  };

  /* ========== CLOSED MODE BUTTON ONLY ========== */
  if (!isOpen) {
    return (
      <button
        className="right-panel-toggle closed"
        onClick={() => setIsOpen(true)}
      >
        <FaBell size={18} />
      </button>
    );
  }

  // Decide what to render based on active tab
  const updates =
    viewMode === VIEW_MODES.LATEST ? latestUpdates : projectOverview;

  const rangeKey = overviewRange; // 'today' | 'yesterday' | 'week' | 'month' | 'year'

  return (
    <div className="right-panel" style={{ width }}>
      <div className="right-panel-header">
        {/* Top row: tabs + close */}
        <div className="header-top">
          <div className="right-panel-switch">
            <button
              className={
                "switch-btn" + (viewMode === VIEW_MODES.LATEST ? " active" : "")
              }
              onClick={() => setViewMode(VIEW_MODES.LATEST)}
            >
              Latest
            </button>
            <button
              className={
                "switch-btn" +
                (viewMode === VIEW_MODES.OVERVIEW ? " active" : "")
              }
              onClick={() => setViewMode(VIEW_MODES.OVERVIEW)}
            >
              Overview
            </button>
          </div>

          <button
            className="right-panel-close"
            onClick={() => setIsOpen(false)}
          >
            <span className="close-icon">×</span>
          </button>
        </div>

        {/* Second row: range filter only in Overview */}
        {viewMode === VIEW_MODES.OVERVIEW && (
          <div className="right-panel-range-switch">
            {Object.entries(RANGE_LABELS).map(([key, label]) => (
              <button
                key={key}
                className={
                  "range-btn" + (overviewRange === key ? " active" : "")
                }
                onClick={() => setOverviewRange(key)}
              >
                {label}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="updates-list">
        {updates.length === 0 ? (
          <p className="empty-msg">
            {viewMode === VIEW_MODES.LATEST
              ? "No recent activity in the last 14 days…"
              : "No project overview data yet…"}
          </p>
        ) : viewMode === VIEW_MODES.LATEST ? (
          // ===== LATEST VIEW RENDER (using latest-activity payload) =====
          updates.map((u, i) => (
            <div
              key={i}
              className="update-item"
              onClick={() => handleItemClick(u)}
            >
              <p className="update-title">
                {u.model ? u.model.toUpperCase() : "UPDATE"}
                {u.reportId && (
                  <span className="update-subtitle"> · {u.reportId}</span>
                )}
              </p>

              <p className="update-desc">{getUpdateDescription(u)}</p>

              <span className="update-meta">
                {/* Project chip */}
                {u.project && <span className="chip">{u.project}</span>}

                {/* Risk rating chip for UAUC */}
                {u.riskRating && (
                  <span
                    className={`chip risk-${u.riskRating
                      ?.toLowerCase()
                      .replace(/\s+/g, "-")}`}
                  >
                    {u.riskRating}
                  </span>
                )}

                {/* Status chip for UAUC / Violation etc. */}
                {u.status && (
                  <span
                    className={`chip status-${u.status
                      ?.toLowerCase()
                      .replace(/\s+/g, "-")}`}
                  >
                    {u.status}
                  </span>
                )}

                {/* Attendees chip for meetings */}
                {u.model === "meeting" && u.attendeesNos && (
                  <span className="chip chip-soft">
                    {u.attendeesNos} attendees
                  </span>
                )}
              </span>

              <div className="update-footer">
                {u.createdBy && (
                  <span className="update-author">By {u.createdBy}</span>
                )}
                <span className="update-time">{u.submittedAt}</span>
              </div>
            </div>
          ))
        ) : (
          // ===== PROJECT OVERVIEW VIEW RENDER =====
          updates.map((p, i) => {
            const modules = p.modules || {};

            // Build per-module counts for selected range
            const moduleEntries = Object.entries(modules)
              .map(([modKey, counts]) => ({
                modKey,
                count: counts?.[rangeKey] || 0,
              }))
              .filter((m) => m.count > 0);

            // Total submissions for this project in selected range
            const totalForRange = moduleEntries.reduce(
              (sum, m) => sum + m.count,
              0,
            );

            return (
              <div key={p.projectId || i} className="update-item overview-item">
                <p className="update-title">
                  {p.projectName || p.projectId || "Project"}
                </p>

                <p className="update-desc">
                  {RANGE_LABELS[rangeKey]} submissions:{" "}
                  <strong>{totalForRange}</strong>
                </p>

                {/* Per-module chips */}
                <div className="overview-modules">
                  {moduleEntries.map(({ modKey, count }) => (
                    <span key={modKey} className="chip chip-soft">
                      {MODULE_LABELS[modKey] || modKey}:{" "}
                      <strong>{count}</strong>
                    </span>
                  ))}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default RightUpdatesPanel;
